import './i18n/autoTranslate';
/**
 * PDFEditor.jsx — Resume PDF Studio Premium PDF Editor
 *
 * Fix: pdfjs is loaded from CDN via a <script> tag at runtime instead of
 * being imported as an ESM module. This eliminates the worker URL mismatch
 * that caused blank rendering (the #1 cause of "PDF won't show" bugs).
 *
 * Architecture:
 *   PDFEditor        → root shell, state, keyboard shortcuts
 *   useEditorStore   → central hook: tool, zoom, annotations, undo/redo
 *   Toolbar          → top action bar
 *   LeftSidebar      → page thumbnails
 *   PDFViewer        → dual-canvas: PDF render + annotation overlay
 *   RightSidebar     → contextual properties panel
 *   annotationReducer→ undo/redo via past/present/future stacks
 */

import { useState, useRef, useEffect, useReducer } from "react";

// ─── Load pdfjs from CDN (avoids ESM/worker version mismatch) ────────────────
const PDFJS_VERSION = "3.11.174";
const PDFJS_CDN = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;

function usePdfjsLib() {
  const [lib, setLib] = useState(null);

  useEffect(() => {
    // Already loaded (e.g. hot-reload) — reuse
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
      setLib(window.pdfjsLib);
      return;
    }

    const script = document.createElement("script");
    script.src = `${PDFJS_CDN}/pdf.min.js`;
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
      setLib(window.pdfjsLib);
    };
    document.head.appendChild(script);
  }, []);

  return lib;
}

// ─── Tool constants ───────────────────────────────────────────────────────────
const TOOLS = {
  SELECT:    "select",
  TEXT:      "text",
  DRAW:      "draw",
  HIGHLIGHT: "highlight",
  RECTANGLE: "rectangle",
  CIRCLE:    "circle",
  ARROW:     "arrow",
  IMAGE:     "image",
};

const DEFAULT_PROPS = {
  color:       "#fbbf24",
  fontSize:    18,
  fontFamily:  "Arial",
  strokeWidth: 2,
  opacity:     1,
  fillColor:   "transparent",
};

// ─── Annotation undo/redo reducer ─────────────────────────────────────────────
function annReducer(state, action) {
  switch (action.type) {
    case "ADD": {
      const next = [...state.present, { ...action.ann, id: crypto.randomUUID() }];
      return { past: [...state.past, state.present], present: next, future: [] };
    }
    case "DELETE": {
      const next = state.present.filter(a => a.id !== action.id);
      return { past: [...state.past, state.present], present: next, future: [] };
    }
    case "UNDO": {
      if (!state.past.length) return state;
      const prev = state.past[state.past.length - 1];
      return { past: state.past.slice(0, -1), present: prev, future: [state.present, ...state.future] };
    }
    case "REDO": {
      if (!state.future.length) return state;
      const [next, ...rest] = state.future;
      return { past: [...state.past, state.present], present: next, future: rest };
    }
    default: return state;
  }
}

// ─── Central editor state ─────────────────────────────────────────────────────
function useEditorStore() {
  const [tool, setTool]               = useState(TOOLS.SELECT);
  const [zoom, setZoom]               = useState(1.2);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(0);
  const [pdfDoc, setPdfDoc]           = useState(null);
  const [pdfBytes, setPdfBytes]       = useState(null); // original PDF bytes for pdf-lib
  const [props, setProps]             = useState(DEFAULT_PROPS);
  const [selectedId, setSelectedId]   = useState(null);
  const [annState, dispatch]          = useReducer(annReducer, { past: [], present: [], future: [] });

  const addAnnotation    = ann => dispatch({ type: "ADD", ann });
  const deleteAnnotation = id  => dispatch({ type: "DELETE", id });
  const undo = () => dispatch({ type: "UNDO" });
  const redo = () => dispatch({ type: "REDO" });
  const zoomIn  = () => setZoom(z => Math.min(z + 0.25, 4));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));

  return {
    tool, setTool, zoom, zoomIn, zoomOut,
    currentPage, setCurrentPage, totalPages, setTotalPages,
    pdfDoc, setPdfDoc, pdfBytes, setPdfBytes, props, setProps,
    selectedId, setSelectedId,
    annotations: annState.present,
    canUndo: annState.past.length > 0,
    canRedo: annState.future.length > 0,
    addAnnotation, deleteAnnotation, undo, redo,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// TOOLBAR
// ═════════════════════════════════════════════════════════════════════════════
function Toolbar({ store, onUpload, onDownload }) {
  const { tool, setTool, zoom, zoomIn, zoomOut, canUndo, canRedo, undo, redo, pdfDoc } = store;
  const fileRef = useRef(null);

  const groups = [
    [{ id: TOOLS.SELECT,    icon: "↖",  tip: "Select"    }],
    [{ id: TOOLS.TEXT,      icon: "T",  tip: "Text"      },
     { id: TOOLS.HIGHLIGHT, icon: "▮",  tip: "Highlight" },
     { id: TOOLS.DRAW,      icon: "✏",  tip: "Draw"      }],
    [{ id: TOOLS.RECTANGLE, icon: "▭",  tip: "Rectangle" },
     { id: TOOLS.CIRCLE,    icon: "○",  tip: "Circle"    },
     { id: TOOLS.ARROW,     icon: "↗",  tip: "Arrow"     }],
    [{ id: TOOLS.IMAGE,     icon: "🖼", tip: "Image"     }],
  ];

  const s = {
    bar: {
      display:"flex", alignItems:"center", gap:6, padding:"0 16px", height:52,
      background:"linear-gradient(180deg, rgba(255,255,255,0.075), rgba(255,255,255,0.035))", backdropFilter:"blur(24px) saturate(1.5)",
      borderBottom:"1px solid rgba(255,255,255,0.10)",
      position:"relative", zIndex:20, flexShrink:0,
      boxShadow:"0 16px 48px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)",
    },
    brand: { display:"flex", alignItems:"center", gap:9, marginRight:8 },
    logo: {
      width:36, height:36, borderRadius:13, flexShrink:0,
      background:"linear-gradient(135deg,#fbbf24,#94a3b8,#f59e0b)",
      display:"flex", alignItems:"center", justifyContent:"center",
      color:"#fff", fontWeight:800, fontSize:14,
      boxShadow:"0 0 24px rgba(251,191,36,0.28)",
    },
    brandName: { color:"rgba(255,255,255,0.94)", fontWeight:800, fontSize:15, letterSpacing:"-0.4px" },
    sep: { width:1, height:22, background:"rgba(255,255,255,0.09)", margin:"0 5px" },
    toolBtn: (active) => ({
      width:34, height:34, borderRadius:8, border:"none", cursor:"pointer",
      background: active ? "linear-gradient(135deg,rgba(251,191,36,0.20),rgba(148,163,184,0.12))" : "rgba(255,255,255,0.025)",
      color: active ? "#fde68a" : "rgba(255,255,255,0.58)",
      fontSize:15, fontWeight:700,
      outline: active ? "1.5px solid rgba(251,191,36,0.42)" : "1px solid rgba(255,255,255,0.08)",
      transition:"all 0.12s",
    }),
    zoomBox: {
      display:"flex", alignItems:"center", gap:4,
      background:"rgba(255,255,255,0.05)", borderRadius:8, padding:"2px 8px",
    },
    iconBtn: (disabled) => ({
      width:28, height:28, borderRadius:6, border:"none",
      cursor: disabled ? "not-allowed" : "pointer",
      background:"transparent",
      color: disabled ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.55)",
      fontSize:15, fontWeight:700,
    }),
    exportBtn: (enabled) => ({
      padding:"6px 16px", borderRadius:9, border:"none",
      cursor: enabled ? "pointer" : "not-allowed",
      background: enabled ? "linear-gradient(135deg,#fbbf24,#d1d5db,#f59e0b)" : "rgba(255,255,255,0.06)",
      color: enabled ? "#111827" : "rgba(255,255,255,0.2)",
      fontWeight:700, fontSize:12,
      boxShadow: enabled ? "0 10px 30px rgba(251,191,36,0.22)" : "none",
      transition:"all 0.14s",
    }),
    openBtn: {
      padding:"5px 13px", borderRadius:8,
      border:"1px solid rgba(255,255,255,0.12)",
      background:"linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.035))", color:"rgba(255,255,255,0.9)",
      cursor:"pointer", fontSize:12, fontWeight:600,
    },
  };

  return (
    <header style={s.bar}>
      {/* Brand */}
      <div style={s.brand}>
        {/* <div style={s.logo}>P</div> */}
        <span style={s.brandName}>Resume PDF Studio</span>
      </div>

      <div style={s.sep} />

      {/* Upload */}
      <input ref={fileRef} type="file" accept="application/pdf"
        style={{ display:"none" }} onChange={onUpload} />
      <button style={s.openBtn} onClick={() => fileRef.current?.click()}>
        ⬆ Open PDF
      </button>

      <div style={s.sep} />

      {/* Tool groups */}
      {groups.map((group, gi) => (
        <div key={gi} style={{ display:"flex", alignItems:"center", gap:2 }}>
          {group.map(item => (
            <button key={item.id} title={item.tip}
              onClick={() => setTool(item.id)}
              style={s.toolBtn(tool === item.id)}
            >{item.icon}</button>
          ))}
          <div style={s.sep} />
        </div>
      ))}

      {/* Zoom */}
      <div style={s.zoomBox}>
        <button style={s.iconBtn(false)} onClick={zoomOut}>−</button>
        <span style={{ color:"rgba(255,255,255,0.55)", fontSize:12, width:42, textAlign:"center", fontFamily:"monospace" }}>
          {Math.round(zoom * 100)}%
        </span>
        <button style={s.iconBtn(false)} onClick={zoomIn}>+</button>
      </div>

      <div style={s.sep} />

      {/* Undo / Redo */}
      <button style={s.iconBtn(!canUndo)} onClick={undo} title="Undo (Ctrl+Z)">↩</button>
      <button style={s.iconBtn(!canRedo)} onClick={redo} title="Redo (Ctrl+Y)">↪</button>

      <div style={{ flex:1 }} />

      {/* Export */}
      <button style={s.exportBtn(!!pdfDoc)} onClick={onDownload} disabled={!pdfDoc}>
        ⬇ Export PDF
      </button>
    </header>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LEFT SIDEBAR — page thumbnails
// ═════════════════════════════════════════════════════════════════════════════
function LeftSidebar({ pdfDoc, currentPage, setCurrentPage }) {
  const [thumbs, setThumbs] = useState([]);

  useEffect(() => {
    if (!pdfDoc) { setThumbs([]); return; }
    let cancelled = false;

    (async () => {
      const list = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled) return;
        const page = await pdfDoc.getPage(i);
        const vp   = page.getViewport({ scale: 0.2 });
        const cv   = document.createElement("canvas");
        cv.width   = vp.width;
        cv.height  = vp.height;
        await page.render({ canvasContext: cv.getContext("2d"), viewport: vp }).promise;
        list.push({ n: i, url: cv.toDataURL() });
        if (!cancelled) setThumbs([...list]);
      }
    })();

    return () => { cancelled = true; };
  }, [pdfDoc]);

  return (
    <aside style={{
      width:158, flexShrink:0, display:"flex", flexDirection:"column",
      background:"linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0.025))", borderRight:"1px solid rgba(255,255,255,0.10)", backdropFilter:"blur(18px)",
      overflowY:"auto",
    }}>
      <div style={{ padding:"10px 12px 8px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <p style={{ fontSize:10, fontWeight:700, letterSpacing:"0.13em", color:"rgba(255,255,255,0.28)", textTransform:"uppercase", margin:0 }}>Pages</p>
        {pdfDoc && <p style={{ fontSize:11, color:"rgba(255,255,255,0.38)", margin:"3px 0 0" }}>{pdfDoc.numPages} pages</p>}
      </div>

      <div style={{ padding:8, display:"flex", flexDirection:"column", gap:8, flex:1 }}>
        {thumbs.length === 0 && (
          <p style={{ color:"rgba(255,255,255,0.18)", fontSize:11, textAlign:"center", marginTop:28, lineHeight:1.6 }}>
            Open a PDF<br/>to see pages
          </p>
        )}
        {thumbs.map(({ n, url }) => (
          <button key={n} onClick={() => setCurrentPage(n)} style={{
            border: currentPage===n ? "2px solid rgba(251,191,36,0.85)" : "2px solid rgba(255,255,255,0.08)",
            borderRadius:8, overflow:"hidden", cursor:"pointer", padding:0,
            background:"#fff", position:"relative",
            boxShadow: currentPage===n ? "0 0 0 3px rgba(251,191,36,0.18), 0 18px 35px rgba(0,0,0,0.4)" : "none",
            transform: currentPage===n ? "scale(1.04)" : "scale(1)",
            transition:"all 0.14s",
          }}>
            <img src={url} alt={`p${n}`} style={{ width:"100%", display:"block" }} />
            <span style={{
              position:"absolute", bottom:4, left:"50%", transform:"translateX(-50%)",
              background: currentPage===n ? "linear-gradient(135deg,#fbbf24,#f59e0b)" : "rgba(0,0,0,0.55)",
              color: currentPage===n ? "#111827" : "#fff", fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:4,
            }}>{n}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DRAW ANNOTATION — shared between viewer and export
// ═════════════════════════════════════════════════════════════════════════════
function drawAnnotation(ctx, ann, selected) {
  ctx.save();
  ctx.globalAlpha = ann.opacity ?? 1;
  ctx.strokeStyle = ann.color   ?? "#6366f1";
  ctx.lineWidth   = ann.strokeWidth ?? 2;
  ctx.lineCap     = "round";
  ctx.lineJoin    = "round";

  switch (ann.type) {
    case TOOLS.DRAW:
      if (!ann.path?.length) break;
      ctx.beginPath();
      ctx.moveTo(ann.path[0].x, ann.path[0].y);
      ann.path.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
      break;

    case TOOLS.HIGHLIGHT:
      ctx.globalAlpha = 0.38;
      ctx.fillStyle   = ann.color ?? "#eab308";
      ctx.fillRect(ann.x, ann.y, ann.width, ann.height);
      break;

    case TOOLS.RECTANGLE:
      if (ann.fillColor && ann.fillColor !== "transparent") {
        ctx.fillStyle = ann.fillColor;
        ctx.fillRect(ann.x, ann.y, ann.width, ann.height);
      }
      ctx.strokeRect(ann.x, ann.y, ann.width, ann.height);
      break;

    case TOOLS.CIRCLE: {
      const rx = ann.width / 2, ry = ann.height / 2;
      ctx.beginPath();
      ctx.ellipse(ann.x + rx, ann.y + ry, Math.abs(rx), Math.abs(ry), 0, 0, 2 * Math.PI);
      if (ann.fillColor && ann.fillColor !== "transparent") {
        ctx.fillStyle = ann.fillColor;
        ctx.fill();
      }
      ctx.stroke();
      break;
    }

    case TOOLS.ARROW: {
      const { x1, y1, x2, y2 } = ann;
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const hl = 16;
      ctx.beginPath();
      ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.lineTo(x2 - hl * Math.cos(angle - Math.PI / 6), y2 - hl * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - hl * Math.cos(angle + Math.PI / 6), y2 - hl * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
      break;
    }

    case TOOLS.TEXT:
      ctx.globalAlpha = ann.opacity ?? 1;
      ctx.fillStyle   = ann.color;
      ctx.font        = `${ann.fontSize ?? 18}px ${ann.fontFamily ?? "Arial"}`;
      ctx.fillText(ann.text, ann.x, ann.y);
      break;

    case TOOLS.IMAGE:
      if (ann.imageEl) ctx.drawImage(ann.imageEl, ann.x, ann.y, ann.width, ann.height);
      break;

    default: break;
  }

  // Selection indicator
  if (selected) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "#818cf8";
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([5, 3]);
    const bx = ann.x  ?? Math.min(ann.x1 ?? 0, ann.x2 ?? 0);
    const by = ann.y  ?? Math.min(ann.y1 ?? 0, ann.y2 ?? 0);
    const bw = ann.width  ?? Math.abs((ann.x2 ?? 0) - (ann.x1 ?? 0));
    const bh = ann.height ?? Math.abs((ann.y2 ?? 0) - (ann.y1 ?? 0));
    ctx.strokeRect(bx - 5, by - 5, bw + 10, bh + 10);
    ctx.setLineDash([]);
  }

  ctx.restore();
}

// ═════════════════════════════════════════════════════════════════════════════
// PDF VIEWER
// ═════════════════════════════════════════════════════════════════════════════
function PDFViewer({ store, pdfjsLib }) {
  const {
    pdfDoc, zoom, currentPage, setTotalPages,
    tool, props, annotations, addAnnotation: addAnnotationRaw,
    selectedId, setSelectedId,
  } = store;

  // Every annotation captures the zoom it was created at so coordinates can be
  // re-scaled to the current viewer zoom (and to PDF points on export).
  const addAnnotation = (ann) => addAnnotationRaw({ createdZoom: zoom, ...ann });

  const pdfCanvasRef = useRef(null);
  const annCanvasRef = useRef(null);
  const renderTask   = useRef(null);
  const drawingRef   = useRef(false);
  const startRef     = useRef({ x: 0, y: 0 });
  const pathRef      = useRef([]);

  const [size, setSize]         = useState({ w: 0, h: 0 });
  const [editText, setEditText] = useState(null);
  const [loading, setLoading]   = useState(false);

  // ── Render PDF page ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfDoc || !pdfjsLib) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      // Cancel previous render
      if (renderTask.current) {
        try { renderTask.current.cancel(); } catch {}
        renderTask.current = null;
      }

      try {
        const page     = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale: zoom });

        if (cancelled) return;

        const canvas = pdfCanvasRef.current;
        if (!canvas) return;

        canvas.width  = viewport.width;
        canvas.height = viewport.height;
        setSize({ w: viewport.width, h: viewport.height });
        setTotalPages(pdfDoc.numPages);

        const ctx  = canvas.getContext("2d");
        const task = page.render({ canvasContext: ctx, viewport });
        renderTask.current = task;

        await task.promise;
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") console.error("Render error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pdfDoc, currentPage, zoom, pdfjsLib]);

  // ── Redraw annotations ─────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = annCanvasRef.current;
    if (!canvas || !size.w) return;
    canvas.width  = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, size.w, size.h);
    annotations
      .filter(a => a.page === currentPage)
      .forEach(ann => {
        const ratio = zoom / (ann.createdZoom ?? zoom);
        ctx.save();
        ctx.scale(ratio, ratio);
        drawAnnotation(ctx, ann, ann.id === selectedId);
        ctx.restore();
      });
  }, [annotations, size, currentPage, selectedId, zoom]);

  // ── Pointer helpers ────────────────────────────────────────────────────────
  const getPos = e => {
    // Use offsetX/Y for pixel-perfect position relative to the canvas element,
    // regardless of scroll position or parent transforms.
    const canvas = annCanvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
    };
  };

  const hitTest = pos =>
    [...annotations].reverse().find(a => {
      if (a.page !== currentPage) return false;
      const r = zoom / (a.createdZoom ?? zoom);
      const bx = (a.x  ?? Math.min(a.x1 ?? 0, a.x2 ?? 0) - 10) * r;
      const by = (a.y  ?? Math.min(a.y1 ?? 0, a.y2 ?? 0) - 10) * r;
      const bw = ((a.width  ?? Math.abs((a.x2 ?? 0) - (a.x1 ?? 0))) + 20) * r;
      const bh = ((a.height ?? Math.abs((a.y2 ?? 0) - (a.y1 ?? 0))) + 20) * r;
      return pos.x >= bx && pos.x <= bx + bw && pos.y >= by && pos.y <= by + bh;
    });

  const onPointerDown = e => {
    if (!pdfDoc) return;
    const pos = getPos(e);

    if (tool === TOOLS.SELECT) { setSelectedId(hitTest(pos)?.id ?? null); return; }
    if (tool === TOOLS.TEXT)   { setEditText(pos); return; }

    if (tool === TOOLS.IMAGE) {
      const inp = document.createElement("input");
      inp.type = "file"; inp.accept = "image/*";
      inp.onchange = ev => {
        const file = ev.target.files[0]; if (!file) return;
        const reader = new FileReader();
        reader.onload = re => {
          const img = new Image();
          img.src = re.target.result;
          img.onload = () => {
            const w = Math.min(200, size.w * 0.4);
            const h = (img.height / img.width) * w;
            addAnnotation({ type: TOOLS.IMAGE, page: currentPage, x: pos.x - w / 2, y: pos.y - h / 2, width: w, height: h, imageEl: img, ...props });
          };
        };
        reader.readAsDataURL(file);
      };
      inp.click();
      return;
    }

    drawingRef.current = true;
    startRef.current   = pos;
    if (tool === TOOLS.DRAW) pathRef.current = [pos];
    // Capture pointer so drag events keep firing even if pointer leaves canvas
    annCanvasRef.current.setPointerCapture(e.pointerId);
  };

  const onPointerMove = e => {
    if (!drawingRef.current) return;
    const pos    = getPos(e);
    const start  = startRef.current;
    const canvas = annCanvasRef.current;
    const ctx    = canvas.getContext("2d");

    if (tool === TOOLS.DRAW) {
      // Incremental stroke — just draw the new segment, no full redraw needed
      const path = pathRef.current;
      path.push(pos);
      ctx.strokeStyle = props.color;
      ctx.lineWidth   = props.strokeWidth;
      ctx.lineCap     = "round";
      ctx.lineJoin    = "round";
      ctx.globalAlpha = props.opacity;
      ctx.beginPath();
      ctx.moveTo(path[path.length - 2].x, path[path.length - 2].y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
      return;
    }

    // For all shape tools: redraw committed annotations then draw live preview
    if ([TOOLS.HIGHLIGHT, TOOLS.RECTANGLE, TOOLS.CIRCLE, TOOLS.ARROW].includes(tool)) {
      // Redraw base annotations
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      annotations
        .filter(a => a.page === currentPage)
        .forEach(ann => drawAnnotation(ctx, ann, ann.id === selectedId));

      // Draw live preview shape
      const x = Math.min(start.x, pos.x);
      const y = Math.min(start.y, pos.y);
      const w = Math.abs(pos.x - start.x);
      const h = Math.abs(pos.y - start.y);

      ctx.save();
      ctx.strokeStyle = props.color;
      ctx.lineWidth   = props.strokeWidth ?? 2;
      ctx.lineCap     = "round";
      ctx.lineJoin    = "round";
      ctx.globalAlpha = props.opacity ?? 1;

      switch (tool) {
        case TOOLS.HIGHLIGHT:
          ctx.globalAlpha = 0.38;
          ctx.fillStyle   = props.color ?? "#eab308";
          ctx.fillRect(x, y, w, h);
          break;

        case TOOLS.RECTANGLE:
          if (props.fillColor && props.fillColor !== "transparent") {
            ctx.fillStyle = props.fillColor;
            ctx.fillRect(x, y, w, h);
          }
          ctx.strokeRect(x, y, w, h);
          break;

        case TOOLS.CIRCLE: {
          const rx = w / 2, ry = h / 2;
          ctx.beginPath();
          ctx.ellipse(x + rx, y + ry, Math.abs(rx), Math.abs(ry), 0, 0, 2 * Math.PI);
          if (props.fillColor && props.fillColor !== "transparent") {
            ctx.fillStyle = props.fillColor;
            ctx.fill();
          }
          ctx.stroke();
          break;
        }

        case TOOLS.ARROW: {
          const x1 = start.x, y1 = start.y, x2 = pos.x, y2 = pos.y;
          const angle = Math.atan2(y2 - y1, x2 - x1);
          const hl    = 16;
          ctx.beginPath();
          ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
          ctx.lineTo(x2 - hl * Math.cos(angle - Math.PI / 6), y2 - hl * Math.sin(angle - Math.PI / 6));
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - hl * Math.cos(angle + Math.PI / 6), y2 - hl * Math.sin(angle + Math.PI / 6));
          ctx.stroke();
          break;
        }

        default: break;
      }

      ctx.restore();
    }
  };

  const onPointerUp = e => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const pos   = getPos(e);
    const start = startRef.current;
    const x = Math.min(start.x, pos.x), y = Math.min(start.y, pos.y);
    const w = Math.abs(pos.x - start.x), h = Math.abs(pos.y - start.y);

    switch (tool) {
      case TOOLS.DRAW:
        if (pathRef.current.length > 1)
          addAnnotation({ type: TOOLS.DRAW, page: currentPage, path: [...pathRef.current], ...props });
        pathRef.current = [];
        break;
      case TOOLS.HIGHLIGHT:
        if (w > 4 && h > 4) addAnnotation({ type: TOOLS.HIGHLIGHT, page: currentPage, x, y, width: w, height: h, ...props });
        break;
      case TOOLS.RECTANGLE:
        if (w > 4 && h > 4) addAnnotation({ type: TOOLS.RECTANGLE, page: currentPage, x, y, width: w, height: h, ...props });
        break;
      case TOOLS.CIRCLE:
        if (w > 4 && h > 4) addAnnotation({ type: TOOLS.CIRCLE, page: currentPage, x, y, width: w, height: h, ...props });
        break;
      case TOOLS.ARROW:
        addAnnotation({ type: TOOLS.ARROW, page: currentPage, x1: start.x, y1: start.y, x2: pos.x, y2: pos.y, ...props });
        break;
      default: break;
    }
  };

  const cursorMap = {
    [TOOLS.SELECT]:"default", [TOOLS.TEXT]:"text",
    [TOOLS.DRAW]:"crosshair", [TOOLS.HIGHLIGHT]:"crosshair",
    [TOOLS.RECTANGLE]:"crosshair", [TOOLS.CIRCLE]:"crosshair",
    [TOOLS.ARROW]:"crosshair", [TOOLS.IMAGE]:"copy",
  };

  return (
    <main style={{
      flex: 1, overflow: "auto", display: "flex",
      alignItems: "flex-start", justifyContent: "center",
      padding: 36, background: "radial-gradient(circle at 50% 0%, rgba(251,191,36,0.08), transparent 35%), #05070c",
    }}>
      {!pdfDoc && <EmptyState />}

      {pdfDoc && (
        <div style={{ position: "relative", borderRadius: 6, boxShadow: "0 30px 90px rgba(0,0,0,0.78), 0 0 0 1px rgba(255,255,255,0.10), 0 0 60px rgba(251,191,36,0.06)" }}>
          {/* Loading spinner */}
          {loading && (
            <div style={{
              position: "absolute", inset: 0, borderRadius: 6, zIndex: 10,
              background: "rgba(12,12,21,0.65)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                border: "3px solid rgba(124,58,237,0.25)",
                borderTopColor: "#fbbf24",
                animation: "pdfSpin 0.75s linear infinite",
              }} />
            </div>
          )}

          {/* PDF canvas */}
          <canvas ref={pdfCanvasRef} style={{ display: "block", borderRadius: 6 }} />

          {/* Annotation overlay */}
          <canvas
            ref={annCanvasRef}
            style={{ position: "absolute", inset: 0, borderRadius: 6, cursor: cursorMap[tool] ?? "default" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          />

          {/* Inline text input */}
          {editText && (
            <InlineTextInput
              x={editText.x} y={editText.y} props={props}
              onSubmit={text => {
                if (text.trim()) addAnnotation({ type: TOOLS.TEXT, page: currentPage, x: editText.x, y: editText.y, text, ...props });
                setEditText(null);
              }}
              onCancel={() => setEditText(null)}
            />
          )}
        </div>
      )}

      <style>{`@keyframes pdfSpin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}

function InlineTextInput({ x, y, props, onSubmit, onCancel }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);
  useEffect(() => { setTimeout(() => ref.current?.focus(), 30); }, []);

  return (
    <input ref={ref} value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => { if (e.key === "Enter") onSubmit(val); if (e.key === "Escape") onCancel(); }}
      onBlur={() => onSubmit(val)}
      placeholder="Type here…"
      style={{
        position: "absolute", left: x, top: y - (props.fontSize ?? 18),
        background: "transparent", border: "none",
        borderBottom: `1.5px dashed ${props.color}`,
        outline: "none", color: props.color,
        fontSize: props.fontSize, fontFamily: props.fontFamily,
        minWidth: 140, opacity: props.opacity,
        caretColor: props.color, zIndex: 20,
      }}
    />
  );
}

function EmptyState() {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flex:1, userSelect:"none", paddingBottom:60 }}>
      <div style={{
        width:90, height:90, borderRadius:22,
        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.09)",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:38, marginBottom:22, boxShadow:"inset 0 1px 0 rgba(255,255,255,0.06)",
      }}>📄</div>
      <p style={{ color:"rgba(255,255,255,0.6)", fontSize:16, fontWeight:600, margin:0 }}>
        Open a PDF to get started
      </p>
      <p style={{ color:"rgba(255,255,255,0.22)", fontSize:13, margin:"8px 0 0" }}>
        Click "Open PDF" in the toolbar above
      </p>
      <div style={{ display:"flex", gap:10, marginTop:28 }}>
        {["✏ Draw", "🔲 Shapes", "T Text", "⬇ Export"].map(f => (
          <span key={f} style={{
            padding:"4px 11px", borderRadius:7,
            border:"1px solid rgba(255,255,255,0.09)",
            color:"rgba(255,255,255,0.18)", fontSize:11,
          }}>{f}</span>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// RIGHT SIDEBAR
// ═════════════════════════════════════════════════════════════════════════════
function RightSidebar({ store }) {
  const { props, setProps, tool, selectedId, annotations, deleteAnnotation, setSelectedId } = store;
  const upd = (k, v) => setProps(p => ({ ...p, [k]: v }));

  const COLORS = ["#6366f1","#8b5cf6","#ec4899","#ef4444","#f97316","#eab308","#22c55e","#06b6d4","#ffffff","#0f172a"];
  const FONTS  = ["Arial","Georgia","Courier New","Times New Roman","Verdana"];
  const selectedAnn = annotations.find(a => a.id === selectedId);

  const label = {
    fontSize:10, fontWeight:700, letterSpacing:"0.12em",
    color:"rgba(255,255,255,0.28)", textTransform:"uppercase", margin:"0 0 9px",
  };

  return (
    <aside style={{
      width:222, flexShrink:0, display:"flex", flexDirection:"column",
      background:"linear-gradient(180deg,rgba(255,255,255,0.055),rgba(255,255,255,0.025))", borderLeft:"1px solid rgba(255,255,255,0.10)", backdropFilter:"blur(18px)",
      overflowY:"auto",
    }}>
      <div style={{ padding:"10px 14px 8px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
        <p style={{ fontSize:10, fontWeight:700, letterSpacing:"0.13em", color:"rgba(255,255,255,0.28)", textTransform:"uppercase", margin:0 }}>Properties</p>
        <p style={{ fontSize:11, color:"rgba(255,255,255,0.38)", margin:"3px 0 0", textTransform:"capitalize" }}>{tool} tool</p>
      </div>

      <div style={{ padding:14, display:"flex", flexDirection:"column", gap:20 }}>

        {/* Color */}
        <div>
          <p style={label}>Color</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:7 }}>
            {COLORS.map(c => (
              <button key={c} onClick={() => upd("color", c)} style={{
                width:30, height:30, borderRadius:8, border: props.color===c ? "2.5px solid #fff" : "2px solid transparent",
                background: c, cursor:"pointer",
                transform: props.color===c ? "scale(1.18)" : "scale(1)",
                transition:"all 0.13s",
                boxShadow: props.color===c ? `0 0 10px ${c}99` : "none",
              }} />
            ))}
          </div>
          <input type="color" value={props.color} onChange={e => upd("color", e.target.value)}
            style={{ marginTop:9, width:"100%", height:32, borderRadius:8, border:"1px solid rgba(255,255,255,0.1)", background:"rgba(255,255,255,0.05)", cursor:"pointer", padding:2 }} />
        </div>

        {/* Stroke */}
        {[TOOLS.DRAW, TOOLS.RECTANGLE, TOOLS.CIRCLE, TOOLS.ARROW].includes(tool) && (
          <div>
            <p style={label}>Stroke — {props.strokeWidth}px</p>
            <input type="range" min={1} max={20} value={props.strokeWidth}
              onChange={e => upd("strokeWidth", +e.target.value)}
              style={{ width:"100%", accentColor:"#fbbf24" }} />
          </div>
        )}

        {/* Opacity */}
        <div>
          <p style={label}>Opacity — {Math.round(props.opacity * 100)}%</p>
          <input type="range" min={0.1} max={1} step={0.05} value={props.opacity}
            onChange={e => upd("opacity", +e.target.value)}
            style={{ width:"100%", accentColor:"#fbbf24" }} />
        </div>

        {/* Text tools */}
        {tool === TOOLS.TEXT && (
          <>
            <div>
              <p style={label}>Font Size — {props.fontSize}px</p>
              <input type="range" min={8} max={72} value={props.fontSize}
                onChange={e => upd("fontSize", +e.target.value)}
                style={{ width:"100%", accentColor:"#fbbf24" }} />
            </div>
            <div>
              <p style={label}>Font Family</p>
              <select value={props.fontFamily} onChange={e => upd("fontFamily", e.target.value)}
                style={{
                  width:"100%", background:"rgba(255,255,255,0.07)",
                  border:"1px solid rgba(255,255,255,0.1)", borderRadius:8,
                  padding:"5px 8px", color:"rgba(255,255,255,0.82)", fontSize:12, outline:"none",
                }}>
                {FONTS.map(f => <option key={f} value={f} style={{ background:"#1a1a2e" }}>{f}</option>)}
              </select>
            </div>
          </>
        )}

        {/* Fill color */}
        {[TOOLS.RECTANGLE, TOOLS.CIRCLE].includes(tool) && (
          <div>
            <p style={label}>Fill Color</p>
            <input type="color"
              value={props.fillColor === "transparent" ? "#ffffff" : props.fillColor}
              onChange={e => upd("fillColor", e.target.value)}
              style={{ width:"100%", height:32, borderRadius:8, border:"1px solid rgba(255,255,255,0.1)", background:"rgba(255,255,255,0.05)", cursor:"pointer", padding:2 }} />
            <button onClick={() => upd("fillColor", "transparent")}
              style={{ marginTop:7, width:"100%", background:"none", border:"none", color:"rgba(255,255,255,0.32)", fontSize:11, cursor:"pointer" }}>
              ✕ Clear fill
            </button>
          </div>
        )}

        {/* Selected element */}
        {selectedAnn && (
          <div style={{ paddingTop:16, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
            <p style={label}>Selected</p>
            <p style={{ fontSize:11, color:"rgba(255,255,255,0.42)", textTransform:"capitalize", margin:"0 0 11px" }}>{selectedAnn.type}</p>
            <button
              onClick={() => { deleteAnnotation(selectedId); setSelectedId(null); }}
              style={{
                width:"100%", padding:"7px 0", borderRadius:8,
                background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.24)",
                color:"#f87171", fontSize:12, fontWeight:600, cursor:"pointer",
              }}>🗑 Delete</button>
          </div>
        )}
      </div>
    </aside>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// EXPORT — REAL PDF EDITING (pdf-lib)
// ═════════════════════════════════════════════════════════════════════════════
// This loads the ORIGINAL PDF bytes with pdf-lib and draws annotations directly
// onto the existing pages. The result preserves the original text layer — text
// stays selectable/searchable, vectors stay crisp, and file size stays sane.
//
// Coordinate conversion:
//   • Annotation coords are stored in canvas pixels at `viewerZoom`.
//   • pdf-lib uses PDF points with origin at BOTTOM-LEFT.
//   • Conversion: pdfX = canvasX / zoom,  pdfY = pageHeight - (canvasY / zoom)
// ─────────────────────────────────────────────────────────────────────────────

const PDFLIB_CDN = "https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js";

function loadPdfLib() {
  if (window.PDFLib) return Promise.resolve(window.PDFLib);
  return new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = PDFLIB_CDN;
    s.onload  = () => res(window.PDFLib);
    s.onerror = rej;
    document.head.appendChild(s);
  });
}

// Convert "#rrggbb" to pdf-lib rgb() object
function hexToRgb(PDFLib, hex) {
  if (!hex || hex === "transparent") return null;
  const clean = hex.replace("#", "");
  const n = parseInt(clean.length === 3
    ? clean.split("").map(c => c + c).join("")
    : clean, 16);
  return PDFLib.rgb(((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255);
}

async function exportAnnotatedPDF(pdfBytes, annotations, viewerZoom) {
  if (!pdfBytes) { alert("No PDF loaded."); return; }

  const PDFLib = await loadPdfLib();
  const { PDFDocument, StandardFonts } = PDFLib;

  // Clone bytes — pdf-lib can consume the buffer
  const pdfDocLib = await PDFDocument.load(pdfBytes.slice(0));
  const pages     = pdfDocLib.getPages();
  const font      = await pdfDocLib.embedFont(StandardFonts.Helvetica);

  // Cache embedded images (so the same image isn't embedded multiple times)
  const imageCache = new Map();

  for (const ann of annotations) {
    const page = pages[ann.page - 1];
    if (!page) continue;

    const { height: ph } = page.getSize();
    // Each annotation was created at a specific zoom — use THAT to convert to
    // PDF points, not the current viewer zoom.
    const z = ann.createdZoom ?? viewerZoom ?? 1;

    // Canvas → PDF-point helpers (flip Y)
    const toX = cx => cx / z;
    const toY = cy => ph - cy / z;
    const toW = cw => cw / z;

    const stroke = hexToRgb(PDFLib, ann.color);
    const fill   = hexToRgb(PDFLib, ann.fillColor);
    const sw     = (ann.strokeWidth ?? 2) / z;
    const opacity = ann.opacity ?? 1;

    switch (ann.type) {
      case TOOLS.TEXT: {
        const fontSize = (ann.fontSize ?? 18) / z;
        // Canvas fillText baseline is alphabetic — y is the baseline.
        // For pdf-lib, drawText y is also the baseline, so simple flip works.
        page.drawText(ann.text ?? "", {
          x: toX(ann.x),
          y: toY(ann.y),
          size: fontSize,
          font,
          color: stroke || PDFLib.rgb(0, 0, 0),
          opacity,
        });
        break;
      }

      case TOOLS.HIGHLIGHT: {
        const highlightColor = hexToRgb(PDFLib, ann.color) || PDFLib.rgb(0.92, 0.70, 0.03);
        page.drawRectangle({
          x: toX(ann.x),
          y: toY(ann.y + ann.height), // bottom-left in PDF space
          width:  toW(ann.width),
          height: toW(ann.height),
          color: highlightColor,
          opacity: 0.38,
        });
        break;
      }

      case TOOLS.RECTANGLE: {
        const opts = {
          x: toX(ann.x),
          y: toY(ann.y + ann.height),
          width:  toW(ann.width),
          height: toW(ann.height),
          borderColor: stroke || PDFLib.rgb(0.39, 0.4, 0.95),
          borderWidth: sw,
          opacity,
        };
        if (fill) opts.color = fill;
        page.drawRectangle(opts);
        break;
      }

      case TOOLS.CIRCLE: {
        const rx = toW(ann.width) / 2;
        const ry = toW(ann.height) / 2;
        const cx = toX(ann.x) + rx;
        const cy = toY(ann.y + ann.height) + ry;
        const opts = {
          x: cx, y: cy,
          xScale: Math.abs(rx),
          yScale: Math.abs(ry),
          borderColor: stroke || PDFLib.rgb(0.39, 0.4, 0.95),
          borderWidth: sw,
          opacity,
        };
        if (fill) opts.color = fill;
        page.drawEllipse(opts);
        break;
      }

      case TOOLS.DRAW: {
        if (!ann.path || ann.path.length < 2) break;
        // Draw the freehand path as a series of short line segments
        for (let i = 1; i < ann.path.length; i++) {
          const a = ann.path[i - 1];
          const b = ann.path[i];
          page.drawLine({
            start: { x: toX(a.x), y: toY(a.y) },
            end:   { x: toX(b.x), y: toY(b.y) },
            thickness: sw,
            color: stroke || PDFLib.rgb(0.39, 0.4, 0.95),
            opacity,
          });
        }
        break;
      }

      case TOOLS.ARROW: {
        const { x1, y1, x2, y2 } = ann;
        const sx = toX(x1), sy = toY(y1);
        const ex = toX(x2), ey = toY(y2);
        const color = stroke || PDFLib.rgb(0.39, 0.4, 0.95);

        // Shaft
        page.drawLine({ start: { x: sx, y: sy }, end: { x: ex, y: ey }, thickness: sw, color, opacity });

        // Arrow head (two segments at ±30°, length 16 canvas px)
        const angle = Math.atan2(ey - sy, ex - sx);
        const hl = 16 / z;
        const h1x = ex - hl * Math.cos(angle - Math.PI / 6);
        const h1y = ey - hl * Math.sin(angle - Math.PI / 6);
        const h2x = ex - hl * Math.cos(angle + Math.PI / 6);
        const h2y = ey - hl * Math.sin(angle + Math.PI / 6);
        page.drawLine({ start: { x: ex, y: ey }, end: { x: h1x, y: h1y }, thickness: sw, color, opacity });
        page.drawLine({ start: { x: ex, y: ey }, end: { x: h2x, y: h2y }, thickness: sw, color, opacity });
        break;
      }

      case TOOLS.IMAGE: {
        if (!ann.imageEl?.src) break;
        let embedded = imageCache.get(ann.imageEl.src);
        if (!embedded) {
          // Fetch the image bytes via the data URL or remote URL
          const res  = await fetch(ann.imageEl.src);
          const buf  = await res.arrayBuffer();
          const src  = ann.imageEl.src;
          embedded = src.includes("image/png") || src.toLowerCase().endsWith(".png")
            ? await pdfDocLib.embedPng(buf)
            : await pdfDocLib.embedJpg(buf);
          imageCache.set(src, embedded);
        }
        page.drawImage(embedded, {
          x: toX(ann.x),
          y: toY(ann.y + ann.height),
          width:  toW(ann.width),
          height: toW(ann.height),
          opacity,
        });
        break;
      }

      default: break;
    }
  }

  const out  = await pdfDocLib.save();
  const blob = new Blob([out], { type: "application/pdf" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "edited-document.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT — PDFEditor
// ═════════════════════════════════════════════════════════════════════════════
export default function PDFEditor() {
  const pdfjsLib = usePdfjsLib();   // CDN-loaded — no worker mismatch possible
  const store    = useEditorStore();

  const handleUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    if (!pdfjsLib) { alert("PDF engine still loading — please wait a moment and try again."); return; }

    // Reset so the same file can be re-opened
    e.target.value = "";

    const buffer = await file.arrayBuffer();
    try {
      // pdfjs consumes the buffer, so keep a copy for pdf-lib export
      const bytesForEditing = buffer.slice(0);
      const doc = await pdfjsLib.getDocument({ data: buffer }).promise;
      store.setPdfDoc(doc);
      store.setPdfBytes(bytesForEditing);
      store.setCurrentPage(1);
    } catch (err) {
      console.error("PDF load error:", err);
      alert("Could not open this PDF. Make sure it's a valid, unencrypted PDF file.");
    }
  };

  const handleDownload = () => exportAnnotatedPDF(store.pdfBytes, store.annotations, store.zoom);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") { e.preventDefault(); e.shiftKey ? store.redo() : store.undo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === "y") { e.preventDefault(); store.redo(); }
      if ((e.key === "Delete" || e.key === "Backspace") && store.selectedId) {
        store.deleteAnnotation(store.selectedId);
        store.setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [store]);

  return (
    <div style={{
      display:"flex", flexDirection:"column", height:"100vh", width:"100vw",
      overflow:"hidden", color:"#fff", fontFamily:"system-ui,-apple-system,sans-serif",
      background:"#05070c"
    }}>
      

      <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", height:"100vh" }}>
        <Toolbar store={store} onUpload={handleUpload} onDownload={handleDownload} />

        <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
          <LeftSidebar
            pdfDoc={store.pdfDoc}
            currentPage={store.currentPage}
            setCurrentPage={store.setCurrentPage}
          />
          <PDFViewer store={store} pdfjsLib={pdfjsLib} />
          <RightSidebar store={store} />
        </div>

        {/* Status bar */}
        <footer style={{
          height:24, display:"flex", alignItems:"center", padding:"0 16px", gap:12,
          background:"rgba(255,255,255,0.02)", borderTop:"1px solid rgba(255,255,255,0.05)",
          fontSize:10, color:"rgba(255,255,255,0.22)", flexShrink:0,
        }}>
          {store.pdfDoc
            ? <>
                <span>Page {store.currentPage} / {store.totalPages}</span>
                <span style={{ opacity:0.4 }}>·</span>
                <span>Zoom {Math.round(store.zoom * 100)}%</span>
                <span style={{ opacity:0.4 }}>·</span>
                <span>{store.annotations.filter(a => a.page === store.currentPage).length} annotation(s) on this page</span>
              </>
            : <span style={{ color: pdfjsLib ? "rgba(255,255,255,0.22)" : "#f97316" }}>
                {pdfjsLib ? "No document loaded — click Open PDF" : "⏳ Loading PDF engine…"}
              </span>
          }
          <div style={{ flex:1 }} />
          <span>InterviewGenieAI PDF Editor</span>
        </footer>
      </div>
    </div>
  );
}
